import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiDollarSign, FiGrid, FiFileText, FiPieChart, FiTrendingUp, FiShield, FiUsers, FiBarChart2 } from 'react-icons/fi';
import { useModal } from '../services/ModalService';
import './FinancialServices.css';

const FinancialServices = () => {
  const [activeCard, setActiveCard] = useState(null);
  const navigate = useNavigate();
  const { openContactModal } = useModal();

  const services = [
    {
      id: 1,
      icon: <FiGrid />,
      title: "Bookkeeping Accounting",
      description: "Enhance your CPA firm's capacity with our comprehensive bookkeeping support that seamlessly integrates with your practice.",
      features: ["Client-Branded Reports", "Scalable Processing", "Quality Control", "Fast Turnaround"],
      color: "#4F46E5",
      route: "/bookkeeping-accounting"
    },
    {
      id: 2,
      icon: <FiFileText />,
      title: "Tax Preparation",
      description: "Expand your firm's tax capabilities with specialized preparation services for complex corporate and multi-state returns.",
      features: ["Complex Entity Returns", "Multi-State Compliance", "R&D Credit Prep", "Tax Research Support"],
      color: "#059669",
      route: "/tax-preparation"
    },
    {
      id: 3,
      icon: <FiPieChart />,
      title: "Payroll Management",
      description: "Offer comprehensive payroll services to your clients with our enterprise-grade processing technology and support.",
      features: ["Multi-Client Platform", "Compliance Management", "Benefits Integration", "Client Self-Service"],
      color: "#DC2626",
      route: "/payroll-management"
    },
    {
      id: 4,
      icon: <FiTrendingUp />,
      title: "Equity Management",
      description: "Provide advanced financial insights to your clients with our sophisticated analysis and reporting capabilities.",
      features: ["Management Reports", "KPI Dashboards", "Trend Analysis", "Custom Analytics"],
      color: "#7C3AED",
      route: "/equity-management"
    },
    {
      id: 5,
      icon: <FiShield />,
      title: "Fund Administration",
      description: "Stay ahead of regulatory requirements with our specialized compliance monitoring and risk assessment services.",
      features: ["Regulatory Updates", "Audit Preparation", "Risk Assessment", "SOX Compliance"],
      color: "#EA580C",
      route: "/fund-administration"
    },
    {
      id: 6,
      icon: <FiUsers />,
      title: "Staffing",
      description: "Strategic talent acquisition for C-suite executives and technical specialists",
      features: ["Executive Search", "Technical Recruiting", "Contract-to-Hire", "Talent Pipeline"],
      color: "#059669",
      route: "/staffing"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const handleLearnMore = (route) => {
    navigate(route);
    window.scrollTo(0, 0);
  };

  return (
    <section className="financial-services">
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="section-badge">
            <FiDollarSign />
            <span>For CPA Firms</span>
          </div>
          <h2 className="section-title">
            Scale Your <span className="gradient-text">Accounting Practice</span>
          </h2>
          <p className="section-subtitle">
            Enhance your firm's capabilities with our white-label financial services designed specifically for CPA practices
          </p>
        </motion.div>

        <motion.div 
          className="services-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {services.map((service) => (
            <motion.div
              key={service.id}
              className={`service-card ${activeCard === service.id ? 'active' : ''}`}
              variants={cardVariants}
              whileHover={{ 
                y: -10,
                transition: { duration: 0.3 }
              }}
              onHoverStart={() => setActiveCard(service.id)}
              onHoverEnd={() => setActiveCard(null)}
            >
              <div className="card-header">
                <div 
                  className="service-icon"
                  style={{ backgroundColor: `${service.color}15`, color: service.color }}
                >
                  {service.icon}
                </div>
                <h3 className="service-title">{service.title}</h3>
              </div>
              
              <p className="service-description">{service.description}</p>
              
              <div className="service-features">
                {service.features.map((feature, index) => (
                  <motion.div 
                    key={index}
                    className="feature-item"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="feature-dot" style={{ backgroundColor: service.color }}></div>
                    <span>{feature}</span>
                  </motion.div>
                ))}
              </div>

              <motion.button 
                className="service-btn"
                style={{ 
                  background: `linear-gradient(135deg, ${service.color}, ${service.color}DD)` 
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleLearnMore(service.route)}
              >
                Learn More
              </motion.button>

              <div 
                className="card-glow"
                style={{ backgroundColor: service.color }}
              ></div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div 
          className="services-cta"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="cta-content">
            <h3>Ready to Scale Your CPA Practice?</h3>
            <p>Discover how our services can help your firm serve more clients efficiently</p>
            <motion.button 
              className="cta-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => openContactModal(
                'CPA Firm Consultation',
                'Financial Services Section - Schedule Firm Consultation'
              )}
            >
              <FiBarChart2 />
              Schedule Firm Consultation
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FinancialServices; 